import { DailyForecast, HourlyForecast } from "@bienesis/common";
import { Droplet, Thermometer } from "lucide-react";
import { DateTime } from "luxon";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import tw from "twin.macro";

import {
  useWeatherOverviewContext,
  WeatherOverviewDataLoader,
} from "./data-loaders/weather-overview-data-loader";

type ForecastTab = "day" | "days";

const formatTemp = (value: number) => `${Math.round(value)}°`;

const formatMm = (value: number) =>
  value === 0
    ? "0"
    : value.toLocaleString("fr-FR", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      });

/** Sélectionne l'heure courante dans la série horaire (sinon la plus proche). */
const pickCurrentHour = (hourly: HourlyForecast[]): HourlyForecast | null => {
  if (hourly.length === 0) return null;
  const now = DateTime.local();
  return hourly.reduce((closest, point) => {
    const diff = Math.abs(DateTime.fromISO(point.time).diff(now).toMillis());
    const closestDiff = Math.abs(
      DateTime.fromISO(closest.time).diff(now).toMillis()
    );
    return diff < closestDiff ? point : closest;
  }, hourly[0]);
};

const Toggle = ({
  activeTab,
  daysCount,
  onChange,
}: {
  activeTab: ForecastTab;
  daysCount: number;
  onChange: (tab: ForecastTab) => void;
}) => {
  const { t } = useTranslation();
  return (
    <div tw="flex items-center gap-2 px-4 py-6">
      {[
        { key: "day" as const, label: t("meteo.forecast.day") },
        {
          key: "days" as const,
          label: t("meteo.forecast.days", { count: daysCount }),
        },
      ].map(({ key, label }) => (
        <button
          key={key}
          type="button"
          onClick={() => onChange(key)}
          css={[
            tw`flex-1 rounded-full border px-4 py-2 text-sm font-medium transition`,
            activeTab === key
              ? tw`border-primary-700 text-primary-800 bg-primary-100`
              : tw`border-transparent text-gray-500`,
          ]}
        >
          {label}
        </button>
      ))}
    </div>
  );
};

const CurrentCard = ({
  current,
  today,
}: {
  current: HourlyForecast | null;
  today: DailyForecast | null;
}) => {
  const { t } = useTranslation();
  return (
    <div tw="mx-4 mt-2 rounded-2xl bg-primary-100 border border-primary-200 p-4">
      <div tw="flex items-center gap-4">
        <span tw="text-5xl leading-none">{current?.emoji ?? today?.emoji}</span>
        <p tw="text-5xl font-semibold text-primary-900">
          {current ? formatTemp(current.temperature) : "—"}
        </p>
        {today && (
          <p tw="ml-auto text-sm text-gray-500">
            {formatTemp(today.temperatureMin)} /{" "}
            {formatTemp(today.temperatureMax)}
          </p>
        )}
      </div>
      <div tw="mt-3 flex items-center gap-4 border-t border-primary-200 pt-3 text-sm text-gray-600">
        <span tw="flex items-center gap-1.5">
          <Droplet tw="w-4 h-4 text-blue-500" />
          {current ? `${formatMm(current.precipitation)} mm` : "—"}
        </span>
        <span tw="flex items-center gap-1.5">
          <Thermometer tw="w-4 h-4 text-red-400" />
          {current ? formatTemp(current.temperature) : "—"}
        </span>
        <span tw="ml-auto text-xs text-gray-400">
          {t("meteo.forecast.current")}
        </span>
      </div>
    </div>
  );
};

const ROW_GRID = tw`grid grid-cols-[3rem_2.5rem_1fr_auto] items-center gap-3`;

const HourlyTable = ({ hourly }: { hourly: HourlyForecast[] }) => {
  const { t } = useTranslation();
  return (
    <div tw="px-4 pb-2">
      <div
        css={[
          ROW_GRID,
          tw`px-3 pb-2 text-xs font-medium text-gray-400 uppercase tracking-wide`,
        ]}
      >
        <span>{t("meteo.forecast.hour")}</span>
        <span />
        <span>{t("meteo.forecast.precipitation")}</span>
        <span tw="text-right">{t("meteo.forecast.temperature")}</span>
      </div>
      <div tw="rounded-xl border border-gray-100 overflow-hidden">
        {hourly.map((point, i) => (
          <div
            key={point.time}
            css={[
              ROW_GRID,
              tw`px-3 py-2.5 text-sm text-gray-700`,
              i % 2 === 0 && tw`bg-primary-50`,
            ]}
          >
            <span tw="font-medium tabular-nums">
              {DateTime.fromISO(point.time).toFormat("HH")} h
            </span>
            <span tw="text-center text-lg leading-none">{point.emoji}</span>
            <span tw="text-gray-500">{formatMm(point.precipitation)} mm</span>
            <span tw="text-right font-medium tabular-nums">
              {formatTemp(point.temperature)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const DailyList = ({ daily }: { daily: DailyForecast[] }) => (
  <div tw="px-4 pb-2">
    <div tw="rounded-xl border border-gray-100 overflow-hidden">
      {daily.map((day, i) => (
        <div
          key={day.date}
          css={[
            tw`flex items-center gap-3 px-3 py-3 text-sm text-gray-700`,
            i % 2 === 0 && tw`bg-primary-50`,
          ]}
        >
          <span tw="text-xl leading-none">{day.emoji}</span>
          <span tw="font-medium capitalize">
            {DateTime.fromISO(day.date).setLocale("fr").toFormat("ccc dd/MM")}
          </span>
          <span tw="ml-auto tabular-nums">
            <span tw="text-gray-400">{formatTemp(day.temperatureMin)}</span>
            <span tw="mx-1 text-gray-300">/</span>
            <span tw="font-semibold text-gray-700">
              {formatTemp(day.temperatureMax)}
            </span>
          </span>
        </div>
      ))}
    </div>
  </div>
);

const WeatherForecastContent = () => {
  const { data } = useWeatherOverviewContext();
  const [activeTab, setActiveTab] = useState<ForecastTab>("day");

  const current = useMemo(() => pickCurrentHour(data.hourly), [data.hourly]);
  const today = data.daily[0] ?? null;

  return (
    <div tw="flex flex-col pb-6">
      <CurrentCard current={current} today={today} />
      <Toggle
        activeTab={activeTab}
        daysCount={data.daily.length}
        onChange={setActiveTab}
      />
      {activeTab === "day" ? (
        <HourlyTable hourly={data.hourly} />
      ) : (
        <DailyList daily={data.daily} />
      )}
    </div>
  );
};

export default function WeatherForecastSheetBody({
  areaId,
}: {
  areaId: string;
}) {
  return (
    <WeatherOverviewDataLoader areaId={areaId}>
      <WeatherForecastContent />
    </WeatherOverviewDataLoader>
  );
}
